import { useState, useEffect } from "react";
import {
  useCreateSchemeIssue,
  useOneSchemeIssue,
  useUpdateSchemeIssue,
  useOrdersForRef,
  useLastBatchId,
} from "http-hooks/schIssue/useSchemeIssue";
import Input from "components/ui/Input";
import SelectVendors from "components/ui/selectors/SelectVendors";
import SelectCustomers from "components/ui/selectors/SelectCustomers";
import { useAllSchemeGiftItems } from "http-hooks/schmitemaster/useSchemeGiftItem";
import Select from "components/ui/Select";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import authHeader from "services/auth-header";
import { TrashIcon, PlusIcon } from "@heroicons/react/24/outline";
import Modal from "components/ui/modals";
import { getSchemeItem } from "services/agitem";
import { useNavigate } from "react-router-dom";

const typeOptions = [
  { value: "B", label: "Bill / Issue" },
  { value: "O", label: "Order" },
];

const emptyItemRow = {
  issd_vchno: null,
  schitemid: "",
  itmqty: "",
  itmamt: "",
  issu_remark: "",
  ref_ord: 0,
  _giftItemSelect: null,
};

const FormSkeleton = () => (
  <div className="animate-pulse">
    <div className="h-7 bg-gray-200 rounded w-1/4 mb-8"></div>
    <div className="space-y-6">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <div key={i}>
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
          <div className="h-9 bg-gray-200 rounded w-full"></div>
        </div>
      ))}
    </div>
  </div>
);

const useSchemesForVendor = (vendorId, enabled = true) => {
  return useQuery({
    queryKey: ["schemes", vendorId],
    queryFn: async () => {
      if (!vendorId) return [];
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/schememaster?noPagination=true&vendor=${vendorId}`,
        { headers: authHeader() }
      );
      return response.data.data.data || [];
    },
    enabled: Boolean(vendorId) && enabled,
  });
};

const useGiftItemsForScheme = (schemeId, enabled = true) => {
  return useQuery({
    queryKey: ["schemeGiftItems", schemeId],
    queryFn: async () => {
      if (!schemeId) return [];
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/schemedetail/${schemeId}`,
        { headers: authHeader() }
      );
      return response.data.data.giftDetails || [];
    },
    enabled: Boolean(schemeId) && enabled,
  });
};

export default function SetSchemeIssue({ id, onClose, open }) {
  const navigate = useNavigate();

  const [commonFields, setCommonFields] = useState({
    schdate: "",
    custid: "",
    schmId: "",
    type: "B",
    recon: 0,
  });

  const [items, setItems] = useState([{ ...emptyItemRow }]);

  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedScheme, setSelectedScheme] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedType, setSelectedType] = useState(typeOptions[0]);
  const [selectedRefOrders, setSelectedRefOrders] = useState([]);
  const [partyDocNo, setPartyDocNo] = useState("");
  const [createBillEntry, setCreateBillEntry] = useState(false);
  const [isNavigatingToBill, setIsNavigatingToBill] = useState(false);

  // Reset all form state when switching to create mode (id becomes null/undefined)
  useEffect(() => {
    if (!id) {
      setCommonFields({ schdate: "", custid: "", schmId: "", type: "B", recon: 0 });
      setItems([{ ...emptyItemRow }]);
      setSelectedVendor(null);
      setSelectedScheme(null);
      setSelectedCustomer(null);
      setSelectedType(typeOptions[0]);
      setSelectedRefOrders([]);
      setPartyDocNo("");
      setCreateBillEntry(false);
    }
  }, [id]);

  const { isLoading: isLoadingIssue, data } = useOneSchemeIssue(id);
  const { mutateAsync: mutateCreate, isPending: isCreating } =
    useCreateSchemeIssue();
  const { mutateAsync: mutateUpdate, isPending: isUpdating } =
    useUpdateSchemeIssue();

  const { data: schemes = [], isLoading: isSchemesLoading } =
    useSchemesForVendor(selectedVendor?.ps_Id);

  const { data: giftItems = [], isLoading: isGiftItemsLoading } =
    useGiftItemsForScheme(selectedScheme?.value);

  const { data: { data: allGiftItems = [] } = {} } = useAllSchemeGiftItems({
    noPagination: true,
  });

  const { data: lastBatchIdData, isLoading: isLoadingLastBatchId } =
    useLastBatchId(commonFields.type);

  const { data: ordersForRef = [] } = useOrdersForRef(
    {
      schmId: commonFields.schmId,
      custid: commonFields.custid,
    },
    commonFields.type === "B" && Boolean(commonFields.schmId)
  );

  const handleCommonChange = (e) => {
    const { name, value } = e.target;
    setCommonFields((prev) => ({ ...prev, [name]: value }));
  };

  const handleVendorChange = (vendor) => {
    setSelectedVendor(vendor);
    setSelectedScheme(null);
    setCommonFields((prev) => ({ ...prev, schmId: "" }));
    setItems([{ ...emptyItemRow }]);
  };

  const handleSchemeChange = (scheme) => {
    setSelectedScheme(scheme);
    setCommonFields((prev) => ({ ...prev, schmId: scheme?.value || "" }));
    setItems([{ ...emptyItemRow }]);
    setSelectedRefOrders([]);
  };

  const handleCustomerChange = (customer) => {
    setSelectedCustomer(customer);
    setCommonFields((prev) => ({
      ...prev,
      custid: customer?.ps_Id || "",
    }));
    setSelectedRefOrders([]);
  };

  const handleTypeChange = (option) => {
    setSelectedType(option);
    setCommonFields((prev) => ({
      ...prev,
      type: option?.value || "B",
    }));
    setSelectedRefOrders([]);
    setItems([{ ...emptyItemRow }]);
  };

  const resolveGiftItemLabel = (schitemid) => {
    const matchingGiftItem = giftItems.find((gi) => gi.schitmid === schitemid);
    if (matchingGiftItem) {
      const details = allGiftItems.find(
        (gi) => gi.schmitemId === matchingGiftItem.schitmid
      );
      return {
        value: matchingGiftItem.schitmid,
        label: details
          ? `${details.schmitemname} | Rs. ${details.schm_value}`
          : `Gift Item ${matchingGiftItem.schitmid}`,
      };
    }
    return null;
  };

  const handleRefOrdersChange = (options) => {
    const selected = options || [];
    setSelectedRefOrders(selected);

    if (selected.length === 0) {
      setItems([{ ...emptyItemRow }]);
      return;
    }

    const allItems = [];
    for (const opt of selected) {
      const order = ordersForRef.find((o) => o.first_vchno === opt.value);
      if (order && order.items.length > 0) {
        for (const item of order.items) {
          allItems.push({
            schitemid: item.schitemid,
            itmqty: item.itmqty,
            itmamt: item.itmamt || "",
            issu_remark: item.issu_remark || "",
            ref_ord: order.first_vchno,
            _giftItemSelect: resolveGiftItemLabel(item.schitemid),
          });
        }
      }
    }

    if (allItems.length > 0) {
      setItems(allItems);
    } else {
      setItems([{ ...emptyItemRow }]);
    }
  };

  const handleItemChange = (index, field, value) => {
    setItems((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleGiftItemSelectChange = (index, option) => {
    setItems((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        schitemid: option?.value || "",
        _giftItemSelect: option,
      };
      return updated;
    });
  };

  const addItemRow = () => {
    setItems((prev) => [...prev, { ...emptyItemRow }]);
  };

  const removeItemRow = (index) => {
    if (items.length <= 1) return;
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  // Populate form in edit mode
  useEffect(() => {
    if (data) {
      setCommonFields({
        schdate: data.schdate || "",
        custid: data.custid || "",
        schmId: data.schmId || "",
        type: data.type || "B",
        recon: data.recon || 0,
      });

      setSelectedType(
        typeOptions.find((t) => t.value === (data.type || "B")) || typeOptions[0]
      );

      if (data.vendorData) {
        setSelectedVendor(data.vendorData);
      }

      if (data.schemeData) {
        setSelectedScheme({
          value: data.schemeData.schemeid,
          label: `${data.schemeData.sch_name} - ${data.schemeData.sch_date}`,
        });
      }

      if (data.customerDetail) {
        setSelectedCustomer(data.customerDetail);
      }

      if (data.items && data.items.length > 0) {
        setPartyDocNo(data.items[0].schdocno || "");
        setItems(
          data.items.map((item) => ({
            issd_vchno: item.issd_vchno || null,
            schitemid: item.schitemid || "",
            itmqty: item.itmqty || "",
            itmamt: item.itmamt || "",
            issu_remark: item.issu_remark || "",
            ref_ord: item.ref_ord || 0,
            _giftItemSelect: null,
          }))
        );
      }
    }
  }, [data]);

  // Resolve gift item labels once gift items are loaded in edit mode
  useEffect(() => {
    if (
      data?.items &&
      giftItems.length > 0 &&
      !isGiftItemsLoading &&
      allGiftItems.length > 0
    ) {
      setItems((prev) =>
        prev.map((row) => {
          if (row.schitemid && !row._giftItemSelect) {
            const matchingGiftItem = giftItems.find(
              (gi) => gi.schitmid === row.schitemid
            );
            if (matchingGiftItem) {
              const details = allGiftItems.find(
                (gi) => gi.schmitemId === matchingGiftItem.schitmid
              );
              return {
                ...row,
                _giftItemSelect: {
                  value: matchingGiftItem.schitmid,
                  label: details
                    ? `${details.schmitemname} | Rs. ${details.schm_value}`
                    : `Gift Item ${matchingGiftItem.schitmid}`,
                },
              };
            }
          }
          return row;
        })
      );
    }
  }, [data, giftItems, isGiftItemsLoading, allGiftItems]);

  // Pre-select ref orders in edit mode for Issue type
  useEffect(() => {
    if (data?.type === "B" && data?.items && ordersForRef.length > 0) {
      const refOrdIds = [
        ...new Set(
          data.items
            .map((item) => item.ref_ord)
            .filter((r) => r && r > 0)
        ),
      ];

      if (refOrdIds.length > 0) {
        const preSelected = refOrdIds
          .map((refId) => {
            const order = ordersForRef.find((o) => o.first_vchno === refId);
            if (order) {
              return {
                value: order.first_vchno,
                label: `${order.schdate} - ${order.customerName} (${order.items.length} items)`,
              };
            }
            return null;
          })
          .filter(Boolean);

        setSelectedRefOrders(preSelected);
      }
    }
  }, [data, ordersForRef]);

  const isPending = isCreating || isUpdating || isNavigatingToBill;

  const schemeOptions = schemes.map((scheme) => ({
    value: scheme.schemeid,
    label: `${scheme.sch_name} - ${scheme.sch_date}`,
  }));

  const giftItemOptions = giftItems.map((item) => {
    const giftItemDetails = allGiftItems.find(
      (gi) => gi.schmitemId === item.schitmid
    );
    return {
      value: item.schitmid,
      label: giftItemDetails
        ? `${giftItemDetails.schmitemname} | Rs. ${giftItemDetails.schm_value}`
        : `Gift Item ${item.schitmid}`,
    };
  });

  const refOrderOptions = ordersForRef.map((order) => ({
    value: order.first_vchno,
    label: `${order.batch_id || order.first_vchno} | ${order.schdate} (${order.items.length} items)`,
  }));

  const handleSubmit = async () => {
    const payload = {
      ...commonFields,
      items: items.map((row) => ({
        issd_vchno: row.issd_vchno || undefined,
        schdocno: partyDocNo || "",
        schitemid: row.schitemid,
        itmqty: row.itmqty,
        itmamt: parseFloat(row.itmamt) || 0,
        issu_remark: row.issu_remark,
        ref_ord: row.ref_ord || 0,
      })),
    };

    try {
      let savedData;
      if (id) {
        savedData = await mutateUpdate({ id, data: payload });
      } else {
        savedData = await mutateCreate(payload);
      }

      if (!id && createBillEntry && isBillType) {
        await handleNavigateToBill(savedData, payload);
      } else {
        onClose();
      }
    } catch (error) {
      console.error("Operation failed:", error);
    }
  };

  const handleNavigateToBill = async (savedData, payload) => {
    try {
      setIsNavigatingToBill(true);

      const totalQty = items.reduce((sum, row) => sum + (parseFloat(row.itmqty) || 0), 0);
      const totalAmt = items.reduce((sum, row) => sum + (parseFloat(row.itmamt) || 0), 0);

      const batchId = Array.isArray(savedData) && savedData.length > 0
        ? savedData[0].batch_id
        : null;

      const schemeItemData = await getSchemeItem(selectedVendor?.ps_Id || "");

      const params = new URLSearchParams({
        fromSchemeIssue: "true",
        billNo: partyDocNo || "",
        remark: batchId || "",
        date: payload.schdate || "",
        customerId: payload.custid || "",
        customerName: selectedCustomer?.name || "",
        vendorId: selectedVendor?.ps_Id || "",
        vendorName: selectedVendor?.name || "",
        totalQty: totalQty.toString(),
        totalAmt: totalAmt.toString(),
        schemeItemId: schemeItemData?.itemid || "",
        schemeItemName: schemeItemData?.item_full_name || "",
        schemeItemUom: schemeItemData?.itemuom || "",
        schemeItemHsn: (schemeItemData?.itmhsnId || 0).toString(),
        schemeItemConval: (schemeItemData?.aguomconval || 1).toString(),
      });

      onClose();
      navigate(`/salebill/new?${params.toString()}`);
    } catch (error) {
      console.error("Failed to navigate to bill:", error);
      onClose();
    } finally {
      setIsNavigatingToBill(false);
    }
  };

  const isBillType = commonFields.type === "B";
  const isOrderType = commonFields.type === "O";

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    await handleSubmit();
  };

  return (
    <Modal
      open={open}
      setOpen={(val) => !val && onClose()}
      maxWidth="4xl"
      header={
        <h3 className="text-base font-semibold leading-6 text-gray-900">
          {id ? "Update" : "Add"} Scheme {isOrderType ? "Order" : "Issue"}
        </h3>
      }
      footer={
        <div className="flex justify-end gap-x-3">
          <button
            type="button"
            className="text-sm font-semibold rounded-md px-3 py-2 hover:bg-gray-100 text-gray-900"
            onClick={onClose}
            disabled={isPending}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="set-schissue-form"
            disabled={isPending}
            className="rounded-md bg-gray-700 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isNavigatingToBill
              ? "Opening Bill..."
              : isPending
              ? "Saving..."
              : !id && createBillEntry && isBillType
              ? "Save & Create Bill"
              : "Save"}
          </button>
        </div>
      }
    >
      {id && isLoadingIssue ? (
        <FormSkeleton />
      ) : (
      <form id="set-schissue-form" onSubmit={handleFormSubmit}>
      <div className="space-y-4">
        <div className="border-b border-gray-900/10 pb-6">
          {/* Common fields */}
          <div className="mt-6 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <Select
                label="Type *"
                value={selectedType}
                onChange={handleTypeChange}
                options={typeOptions}
                isClearable={false}
              />
            </div>

            <div>
              <Input
                type="date"
                name="schdate"
                id="schdate"
                label="Date *"
                value={commonFields.schdate}
                onChange={handleCommonChange}
                now={!id}
                withinFY={true}
                required
              />
            </div>

            {!id && (
              <div>
                <Input
                  type="text"
                  label={`Last ${isOrderType ? "Order" : "Issue"} No.`}
                  value={lastBatchIdData?.lastBatchId || "None"}
                  readOnly
                  placeholder={isLoadingLastBatchId ? "Loading..." : ""}
                />
              </div>
            )}

            {id && data?.batch_id && (
              <div>
                <Input
                  type="text"
                  label={`${isOrderType ? "Order" : "Issue"} No. (Read Only)`}
                  value={data.batch_id}
                  readOnly
                />
              </div>
            )}

            <div>
              <SelectVendors
                label="Vendor *"
                value={selectedVendor}
                onChange={handleVendorChange}
                isClearable={true}
              />
            </div>

            <div>
              <Select
                label="Scheme *"
                value={selectedScheme}
                onChange={handleSchemeChange}
                options={schemeOptions}
                placeholder="Select Scheme"
                isSearchable={true}
                isClearable={true}
                isLoading={isSchemesLoading}
                isDisabled={!selectedVendor}
              />
            </div>

            <div>
              <SelectCustomers
                label="Customer *"
                value={selectedCustomer}
                onChange={handleCustomerChange}
                isClearable={true}
              />
            </div>

            {isBillType && (
              <div>
                <Input
                  type="text"
                  label="Party Doc No"
                  value={partyDocNo}
                  onChange={(e) => setPartyDocNo(e.target.value)}
                  placeholder="Party document number"
                  maxLength={15}
                />
              </div>
            )}

            {isBillType && (
              <div className="sm:col-span-2 lg:col-span-3">
                <Select
                  label="Ref. Orders"
                  value={selectedRefOrders}
                  onChange={handleRefOrdersChange}
                  options={refOrderOptions}
                  placeholder="Select orders (optional, multi)"
                  isSearchable={true}
                  isClearable={true}
                  isMulti={true}
                  isDisabled={!commonFields.schmId}
                />
              </div>
            )}
          </div>
        </div>

        {/* Item rows table */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-900">
              {isOrderType ? "Order" : "Issue"} Gift Items
            </h3>
            <button
              type="button"
              onClick={addItemRow}
              className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-2.5 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-200"
            >
              <PlusIcon className="h-3.5 w-3.5" />
              Add Row
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-10">
                    #
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase min-w-[220px]">
                    {isOrderType ? "Order" : "Issue"} Gift Item *
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase min-w-[100px] w-28">
                    Qty *
                  </th>
                  {isBillType && (
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase min-w-[100px] w-28">
                      Amount
                    </th>
                  )}
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase min-w-[120px]">
                    Remark
                  </th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase w-12">
                    &nbsp;
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {items.map((row, idx) => (
                  <tr key={idx}>
                    <td className="px-3 py-2 text-sm text-gray-500">
                      {idx + 1}
                    </td>
                    <td className="px-3 py-2">
                      <Select
                        value={row._giftItemSelect}
                        onChange={(option) =>
                          handleGiftItemSelectChange(idx, option)
                        }
                        options={giftItemOptions}
                        placeholder="Select Gift Item"
                        isSearchable={true}
                        isClearable={true}
                        isLoading={isGiftItemsLoading}
                        isDisabled={!selectedScheme}
                        menuPortalTarget={document.body}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <Input
                        showLabel={false}
                        type="number"
                        min={0}
                        step="0.001"
                        value={row.itmqty}
                        onChange={(e) =>
                          handleItemChange(idx, "itmqty", e.target.value)
                        }
                        placeholder="0"
                        className="w-full [&_input]:text-right"
                      />
                    </td>
                    {isBillType && (
                      <td className="px-3 py-2">
                        <Input
                          showLabel={false}
                          type="number"
                          min={0}
                          step="0.01"
                          value={row.itmamt}
                          onChange={(e) =>
                            handleItemChange(idx, "itmamt", e.target.value)
                          }
                          placeholder="0.00"
                          className="w-full [&_input]:text-right"
                        />
                      </td>
                    )}
                    <td className="px-3 py-2">
                      <Input
                        showLabel={false}
                        type="text"
                        maxLength={25}
                        value={row.issu_remark}
                        onChange={(e) =>
                          handleItemChange(idx, "issu_remark", e.target.value)
                        }
                        placeholder="Remark"
                        className="w-full"
                      />
                    </td>
                    <td className="px-3 py-2 text-center">
                      {items.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeItemRow(idx)}
                          className="text-red-500 hover:text-red-700 p-1"
                          title="Remove row"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        {!id && isBillType && (
          <div className="flex items-center gap-2 pt-2 border-t border-gray-200">
            <input
              type="checkbox"
              id="createBillEntry"
              checked={createBillEntry}
              onChange={(e) => setCreateBillEntry(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <label
              htmlFor="createBillEntry"
              className="text-sm font-medium text-gray-700 select-none cursor-pointer"
            >
              Bill entry for this issue?
            </label>
          </div>
        )}
      </div>
      </form>
      )}
    </Modal>
  );
}
